import React from "react";
import { motion } from "framer-motion";
import { BottomSheet } from "@/components/ui/bottom-sheet";
import { Check } from "lucide-react";

interface RepairsSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const services = [
  {
    title: "Professional Repairs",
    items: [
      "Expert mending and stitching",
      "Button replacement and reinforcement",
      "Seam repairs and adjustments",
      "Zipper repairs and replacements"
    ]
  },
  {
    title: "Fabric Care",
    items: [
      "Stain removal consultation",
      "Fabric restoration",
      "Color preservation",
      "Special treatment for delicate details"
    ]
  },
  {
    title: "Alterations",
    items: [
      "Size adjustments",
      "Length modifications",
      "Fit refinements",
      "Style updates"
    ]
  }
];

const ServiceSection = ({ 
  title, 
  items 
}: { 
  title: string; 
  items: string[];
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    viewport={{ once: true }}
    className="relative"
  >
    <h3 className="text-lg font-cinzel font-bold text-primary mb-4">{title}</h3>
    <ul className="space-y-2">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
          <Check className="h-4 w-4 mt-1 text-primary flex-shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  </motion.div>
);

export function RepairsSheet({ isOpen, onClose }: RepairsSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose}>
      <div className="px-4">
        {/* Header Section */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-2xl md:text-3xl font-bold mb-3 tracking-wide font-cinzel">
            Repairs & Aftercare
          </h2>
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Preserving the beauty and quality of your TEL U ASEGH pieces.
          </p>
        </motion.div>

        {/* Introduction */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-8 text-center"
        >
          <p className="text-sm text-muted-foreground max-w-2xl mx-auto">
            Our expert artisans are dedicated to maintaining the impeccable quality of your TEL U ASEGH pieces. From minor repairs to comprehensive restorations, we ensure your garments retain their luxury and charm.
          </p>
        </motion.div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-3 gap-8 mb-8">
          {services.map((section, index) => (
            <ServiceSection
              key={index}
              title={section.title}
              items={section.items}
            />
          ))}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary/5 backdrop-blur-sm rounded-2xl p-6 text-center"
        >
          <p className="text-sm text-muted-foreground mb-3">
            Need our repair services? Our team is ready to assist you.
          </p>
          <p className="text-sm font-medium text-primary">
            Contact us at{" "}
            <a 
              href="mailto:cs@teluasegh.com"
              className="hover:underline"
            >
              cs@teluasegh.com
            </a>
          </p>
        </motion.div>
      </div>
    </BottomSheet>
  );
} 